import { useRouter } from '@/routing/useAppRouter';
import { useEffect } from 'react';

import type {
  SolutionResourceFormat,
  SolutionResourceProduct,
} from '@/data/shared/solutionResources';

import type { LanguageType } from '@/types/language';

import {
  type InquiryCtaSource,
  solutionIdForInquiryCtaSource,
} from '@/components/apps/ui/inquiryForm/inquiryCtaSource';
import type { InquiryInterest } from '@/components/apps/ui/inquiryForm/inquiryInterest';

import type { LeadAttribution } from './leadAttribution';

export type AnalyticsEventParameters = Record<
  string,
  string | number | boolean | undefined
>;

type GtagFunction = (
  command: 'config' | 'event',
  targetOrEventName: string,
  parameters?: AnalyticsEventParameters,
) => void;

/**
 * ### LeadEventContext
 *
 * Attribution and form choices shared by every lead event of the inquiry funnel.
 * Carries machine keys only; labels and personal information never reach analytics.
 */
type LeadEventContext = {
  attribution?: LeadAttribution;
  interest?: InquiryInterest;
  selfReportedAiService?: string;
  selfReportedSource?: string;
  source?: InquiryCtaSource;
};

let lastPageLocation: string | null = null;

function definedParameters(parameters: AnalyticsEventParameters) {
  return Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => value !== undefined),
  ) as AnalyticsEventParameters;
}

function getGtag(): GtagFunction | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const gtag = (window as Window & { gtag?: GtagFunction }).gtag;
  return typeof gtag === 'function' ? gtag : null;
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageView = (analyticsId: string, url: string) => {
  if (!analyticsId || typeof window === 'undefined') return;

  const gtag = getGtag();
  if (!gtag) return;

  const pageLocation = new URL(url, window.location.origin).href;
  if (lastPageLocation === pageLocation) return;

  gtag(
    'event',
    'page_view',
    definedParameters({
      page_location: pageLocation,
      page_referrer:
        lastPageLocation ?? (window.document.referrer || undefined),
      page_title: window.document.title,
    }),
  );
  lastPageLocation = pageLocation;
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const event = (
  action: string,
  {
    event_category,
    event_label,
    value,
  }: { event_category: string; event_label: string; value: number },
) => {
  trackEvent(action, {
    event_category,
    event_label,
    value,
  });
};

export const trackEvent = (
  eventName: string,
  parameters: AnalyticsEventParameters = {},
) => {
  const gtag = getGtag();
  if (!gtag) return false;

  gtag('event', eventName, parameters);
  return true;
};

/**
 * ### leadEventParameters
 *
 * Flattens the lead context into GA4 event parameters, omitting every undefined value.
 * `solution_id` is derived from the call-to-action source prefix rather than sent by callers.
 *
 * @param context Attribution and form choices of the current inquiry
 * @returns Event parameters shared by the lead funnel events
 */
function leadEventParameters({
  attribution,
  interest,
  selfReportedAiService,
  selfReportedSource,
  source,
}: LeadEventContext): AnalyticsEventParameters {
  const sessionTouch = attribution?.session_touch;
  return definedParameters({
    conversion_origin: attribution?.conversion_origin,
    cta_source: source,
    interest,
    landing_page: sessionTouch?.landing_page,
    lead_campaign: sessionTouch?.campaign,
    lead_medium: sessionTouch?.medium,
    lead_source: sessionTouch?.source ?? source ?? 'website',
    referrer_domain: sessionTouch?.referrer_domain,
    self_reported_ai_service: selfReportedAiService,
    self_reported_source: selfReportedSource,
    solution_id: solutionIdForInquiryCtaSource(source),
  });
}

export const trackLeadFormStart = (context: LeadEventContext) => {
  trackEvent('lead_form_start', {
    form_id: 'inquiry',
    ...leadEventParameters(context),
  });
};

export const trackLeadFormSubmit = (context: LeadEventContext) => {
  trackEvent('lead_form_submit', {
    form_id: 'inquiry',
    ...leadEventParameters(context),
  });
};

export const trackLeadFormError = ({
  errorType,
  ...context
}: LeadEventContext & { errorType: 'api' | 'network' }) => {
  trackEvent('lead_form_error', {
    error_type: errorType,
    form_id: 'inquiry',
    ...leadEventParameters(context),
  });
};

/**
 * ### generateLead
 *
 * Records the GA4 `generate_lead` conversion once the board API has accepted the inquiry.
 * The lead id is the board row id, so the conversion can be matched to the admin record.
 *
 * @param context Lead context plus the id the API assigned, when it returned one
 */
export const generateLead = ({
  leadId,
  ...context
}: LeadEventContext & { leadId?: string | null }) => {
  trackEvent('generate_lead', {
    ...leadEventParameters(context),
    lead_id: leadId ?? undefined,
  });
};

/**
 * ### SolutionResourceDownloadEvent
 *
 * One reader opening a solution overview or brochure. Every screen that lists
 * resources reports the same shape, so a download can be attributed to a
 * product and a language without knowing which page the link was on.
 */
export type SolutionResourceDownloadEvent = {
  /**
   * ### fileName
   *
   * Hosted file name at the moment of the click. Kept alongside `resourceId`
   * because it is the value that changes when a file is re-exported, which is
   * how a report can tell which revision a reader received.
   */
  fileName: string;

  /**
   * ### fileTitle
   *
   * Title as the reader saw it, already translated into the site language.
   */
  fileTitle: string;

  /**
   * ### language
   *
   * Site language the reader was browsing in, which decides which of the two
   * files the link resolves to.
   */
  language: LanguageType;

  /**
   * ### resourceFormat
   *
   * Whether the reader took the full overview or the short brochure.
   */
  resourceFormat: SolutionResourceFormat;

  /**
   * ### resourceId
   *
   * Stable resource slug, unchanged by a file rename, so download counts stay
   * comparable across revisions of the same document.
   */
  resourceId: string;

  /**
   * ### solutionId
   *
   * Product the document describes, spelled as the solution pages spell it.
   */
  solutionId: SolutionResourceProduct;
};

/**
 * ### trackSolutionResourceDownload
 *
 * Records the GA4 `file_download` event for a solution resource. It is sent on
 * click rather than on delivery: the file is served from another host and the
 * link opens in a new tab, so the site never learns whether the download
 * finished.
 *
 * @param event The resource the reader opened and the language they read it in
 */
export const trackSolutionResourceDownload = ({
  fileName,
  fileTitle,
  language,
  resourceFormat,
  resourceId,
  solutionId,
}: SolutionResourceDownloadEvent) => {
  trackEvent('file_download', {
    file_extension: 'pdf',
    file_name: fileName,
    file_title: fileTitle,
    language,
    resource_format: resourceFormat,
    resource_id: resourceId,
    solution_id: solutionId,
  });
};

// route가 변경될 때 gtag에서
export const useGtag = (analyticsId: string) => {
  const router = useRouter();

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    const handleRouteChange = (url: string) => {
      pageView(analyticsId, url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('hashChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('hashChangeComplete', handleRouteChange);
    };
  }, [router.events, analyticsId]);
};
